import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { FileText, Mail } from "lucide-react";
import { money } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TITLES = {
  tax_invoice: "Tax Invoice",
  gold_bullion: "Gold Bullion Invoice",
};

const Shell = ({ children }) => (
  <div className="min-h-screen bg-[#F3F1EC] px-4 py-8 sm:py-12">
    <div className="mx-auto max-w-3xl">
      <div className="flex items-center justify-center gap-3 pb-7">
        <img src="/brand/logo.png" alt="Dhunna Jewellers" className="h-12 w-auto" />
        <span className="sr-only">Dhunna Jewellers</span>
      </div>
      <div className="border border-[#E0D7C4] bg-white p-6 shadow-[0_1px_0_#E0D7C4] sm:p-10">
        {children}
      </div>
      <p className="mt-6 text-center text-[11px] uppercase tracking-[0.2em] text-[#A08A62]">
        Dhunna Jewellers · Dandenong &amp; Truganina
      </p>
      <p className="mt-2 text-center text-xs text-muted-foreground">
        This page only ever shows your own invoices.{" "}
        <a href="/privacy" className="underline">
          Privacy
        </a>
      </p>
    </div>
  </div>
);

/** Public page — a customer opens this, gets a code by email and sees their own records. */
export default function CustomerPortal() {
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [stage, setStage] = useState("email");
  const [busy, setBusy] = useState(false);
  const [token, setToken] = useState("");
  const [data, setData] = useState(null);

  const askCode = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data: res } = await axios.post(`${API}/public/portal/request`, { email });
      toast.success(res.message);
      setStage("code");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send a code just now");
    } finally {
      setBusy(false);
    }
  };

  const verify = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data: session } = await axios.post(`${API}/public/portal/verify`, {
        email,
        code,
      });
      const { data: overview } = await axios.get(`${API}/public/portal/${session.token}`);
      setToken(session.token);
      setData(overview);
      setStage("list");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not check that code");
    } finally {
      setBusy(false);
    }
  };

  if (stage === "list" && data)
    return (
      <Shell>
        <h1 className="font-invoice text-2xl font-bold" data-testid="portal-heading">
          {data.customer_name ? `Hello ${data.customer_name}` : "Your invoices"}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {data.email} · this page closes itself after 2 hours
        </p>

        <Section title="Invoices" icon={FileText}>
          {data.invoices.length ? (
            data.invoices.map((d) => (
              <Row
                key={d.id}
                testid={`portal-invoice-${d.doc_number}`}
                title={`${TITLES[d.doc_type] || "Invoice"} #${d.doc_number}`}
                sub={`${d.branch} · ${d.doc_date}${d.signed ? " · signed" : ""}`}
                amount={d.total}
                balance={d.balance}
                href={`${API}/public/portal/${token}/document/${d.id}/pdf`}
              />
            ))
          ) : (
            <Empty>No invoices on this email address yet.</Empty>
          )}
        </Section>

        {data.statement && (
          <div className="mt-8 border border-[#C5A059] bg-[#FBF8F2] p-4" data-testid="portal-statement">
            <p className="text-sm font-semibold">Account statement</p>
            <p className="tabular mt-1 text-sm">
              Outstanding balance: <strong>{money(data.statement.totals.outstanding)}</strong>
            </p>
            <a
              href={`${API}/public/portal/${token}/statement`}
              target="_blank"
              rel="noreferrer"
              className="mt-3 inline-flex items-center gap-2 border border-[#C5A059] px-4 py-2 text-sm font-semibold"
              data-testid="portal-statement-pdf"
            >
              <FileText className="h-4 w-4" /> Open full statement (PDF)
            </a>
          </div>
        )}

        <p className="mt-8 text-xs text-muted-foreground">
          Something not right? Call the store and we'll sort it out.
        </p>
      </Shell>
    );

  return (
    <Shell>
      <h1 className="font-invoice text-2xl font-bold">Your invoices</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        {stage === "email"
          ? "Enter the email address we have on your paperwork and we'll send you a 6-digit code."
          : `We emailed a code to ${email}. It expires in 10 minutes.`}
      </p>

      {stage === "email" ? (
        <form onSubmit={askCode} className="mt-6 space-y-4" data-testid="portal-email-form">
          <div className="space-y-1.5">
            <Label className="text-[11px] uppercase tracking-wider">Email</Label>
            <Input
              type="email"
              required
              className="h-11 rounded-none"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="portal-email-input"
            />
          </div>
          <Button
            type="submit"
            disabled={busy}
            className="h-11 w-full rounded-none bg-[#C5A059] font-semibold text-[#0A0A0A] hover:bg-[#B38F4E]"
            data-testid="portal-send-code"
          >
            <Mail className="mr-1.5 h-4 w-4" /> {busy ? "Sending…" : "Email me a code"}
          </Button>
        </form>
      ) : (
        <form onSubmit={verify} className="mt-6 space-y-4" data-testid="portal-code-form">
          <div className="space-y-1.5">
            <Label className="text-[11px] uppercase tracking-wider">6-digit code</Label>
            <Input
              required
              inputMode="numeric"
              maxLength={6}
              className="tabular h-11 rounded-none text-center text-xl tracking-[0.5em]"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              data-testid="portal-code-input"
            />
          </div>
          <Button
            type="submit"
            disabled={busy || code.length < 6}
            className="h-11 w-full rounded-none bg-[#C5A059] font-semibold text-[#0A0A0A] hover:bg-[#B38F4E]"
            data-testid="portal-verify-code"
          >
            {busy ? "Checking…" : "View my invoices"}
          </Button>
          <button
            type="button"
            onClick={() => {
              setStage("email");
              setCode("");
            }}
            className="w-full text-center text-sm text-muted-foreground underline hover:text-[#B38F4E]"
            data-testid="portal-back"
          >
            Use a different email
          </button>
        </form>
      )}
    </Shell>
  );
}

const Section = ({ title, icon: Icon, children }) => (
  <div className="mt-8">
    <p className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
      <Icon className="h-3.5 w-3.5" /> {title}
    </p>
    <div className="mt-3 divide-y divide-border border border-border">{children}</div>
  </div>
);

const Empty = ({ children }) => (
  <p className="px-3 py-4 text-sm text-muted-foreground">{children}</p>
);

const Row = ({ title, sub, amount, balance, href, testid }) => (
  <div className="flex flex-wrap items-center justify-between gap-3 px-3 py-3" data-testid={testid}>
    <div>
      <p className="text-sm font-semibold">{title}</p>
      <p className="text-xs text-muted-foreground">{sub}</p>
    </div>
    <div className="flex items-center gap-4">
      <div className="text-right">
        <p className="tabular text-sm font-semibold">{money(amount)}</p>
        {balance > 0.005 && (
          <p className="tabular text-xs text-[#9A3412]">{money(balance)} due</p>
        )}
      </div>
      <a
        href={href}
        target="_blank"
        rel="noreferrer"
        className="border border-[#C5A059] px-3 py-1.5 text-xs font-semibold"
        data-testid={`${testid}-pdf`}
      >
        PDF
      </a>
    </div>
  </div>
);
